import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { toNumber } from 'lodash'
import { useNow } from '@vueuse/core'
import { appApi } from '@/modules/app/api/app.api'
import { mdiWrenchClock } from '@mdi/js'
import { useAuthStore } from '@/modules/auth/stores/auth.store'
import { useUserStore } from '@/modules/users/stores/user.store'
import { useClientStore } from '@/modules/clients/stores/client.store'
import { useFilterStore } from '@/modules/filter/stores/filter.store'
import { useMotionStore } from '@/modules/motion/stores/motion.store'
import { useDashboardStore } from '@/modules/dashboard/stores/dashboard.store'
import { isWithinInterval, minutesToMilliseconds, subMinutes } from 'date-fns'
import { AppSetting } from '@/modules/app/types/app.enums'
import {
  DEFAULT_MAINTENANCE_ACTIVE_POLL_MINUTES,
  DEFAULT_MAINTENANCE_IDLE_POLL_MINUTES,
  DEFAULT_MAINTENANCE_MAX_IDLE_POLL_MINUTES,
  DEFAULT_MAINTENANCE_MAX_ACTIVE_POLL_MINUTES,
  DEFAULT_MAINTENANCE_MAX_REMINDER_FROM_MINUTES,
  DEFAULT_MAINTENANCE_REMINDER_FROM_MINUTES,
} from '@/modules/app/constants/app.constants'
import type { RouteRecordNameGeneric } from 'vue-router'
import type { AppAlert } from '@/modules/app/models/app.model'

export const useAppStore = defineStore('app', () => {
  const now = useNow({ interval: minutesToMilliseconds(1) })

  const authStore = useAuthStore()
  const userStore = useUserStore()
  const clientStore = useClientStore()
  const filterStore = useFilterStore()
  const motionStore = useMotionStore()
  const dashboardstore = useDashboardStore()

  const activeStart = ref<Date>()
  const activeEnd = ref<Date>()
  const timerId = ref<NodeJS.Timeout>()
  const isLoading = ref<boolean>(false)
  const isIndeterminate = ref<boolean>(false)
  const isNavExpanded = ref<boolean>(false)
  const isFilterOpen = ref<boolean>(false)
  const isInfoOpen = ref<boolean>(false)
  const alerts = ref<AppAlert[]>([])
  const reminderFromMinutes = ref<number>()
  const deliveryNoteB2BUrlTemplate = ref<string>('')
  const deliveryNoteB2CUrlTemplate = ref<string>('')
  const idleInterval = ref<number>(DEFAULT_MAINTENANCE_IDLE_POLL_MINUTES)
  const activeInterval = ref<number>(DEFAULT_MAINTENANCE_ACTIVE_POLL_MINUTES)

  const isMaintenanceActive = computed((): boolean => {
    if (!activeStart.value || !activeEnd.value) return false

    return isWithinInterval(now.value, {
      start: activeStart.value,
      end: activeEnd.value,
    })
  })

  const reminderFrom = computed((): Date | undefined => {
    if (!activeStart.value || !reminderFromMinutes.value) return

    return subMinutes(activeStart.value, reminderFromMinutes.value)
  })

  const isReminderActive = computed((): boolean => {
    if (!reminderFrom.value) return false

    return isWithinInterval(now.value, {
      start: reminderFrom.value,
      end: activeEnd.value!,
    })
  })

  const intervalMinutes = computed((): number =>
    isMaintenanceActive.value || isReminderActive.value ? activeInterval.value : idleInterval.value,
  )

  const parseDate = (value: string | null | undefined): Date | undefined => {
    if (!value) return undefined

    const date = new Date(value)

    return isNaN(date.getTime()) ? undefined : date
  }

  const parseNumberWithFallback = (
    value: string | undefined,
    fallback: number,
    max: number,
  ): number => {
    const number = toNumber(value)

    if (!value || isNaN(number)) return fallback
    if (max !== undefined && number > max) return fallback

    return number
  }
  const showAlert = (alert: AppAlert) => {
    alerts.value.pop()
    alerts.value.push(alert)
  }

  const setMaintenance = (data: Record<AppSetting, string | undefined>): void => {
    if (!data) return

    activeStart.value = parseDate(data[AppSetting.MaintenanceActiveFromUtcFormat])
    activeEnd.value = parseDate(data[AppSetting.MaintenanceActiveToUtcFormat])

    reminderFromMinutes.value = parseNumberWithFallback(
      data[AppSetting.MaintenanceReminderFromMinutes],
      DEFAULT_MAINTENANCE_REMINDER_FROM_MINUTES,
      DEFAULT_MAINTENANCE_MAX_REMINDER_FROM_MINUTES,
    )

    idleInterval.value = parseNumberWithFallback(
      data[AppSetting.MaintenanceIdleIntervalMinutes],
      DEFAULT_MAINTENANCE_IDLE_POLL_MINUTES,
      DEFAULT_MAINTENANCE_MAX_IDLE_POLL_MINUTES,
    )
    activeInterval.value = parseNumberWithFallback(
      data[AppSetting.MaintenanceActiveIntervalMinutes],
      DEFAULT_MAINTENANCE_ACTIVE_POLL_MINUTES,
      DEFAULT_MAINTENANCE_MAX_ACTIVE_POLL_MINUTES,
    )
  }

  const loadAppSettings = async (request: AppSetting[]): Promise<void> => {
    const response = await appApi.getAppSettings(request)

    if (!response.data) return

    deliveryNoteB2BUrlTemplate.value = response.data[AppSetting.DeliveryNoteB2BUrlTemplate]
    deliveryNoteB2CUrlTemplate.value = response.data[AppSetting.DeliveryNoteB2CUrlTemplate]

    setMaintenance(response.data)

    if (isReminderActive.value) {
      showAlert({
        title: 'glossary.scheduledMaintenance',
        icon: mdiWrenchClock,
        from: activeStart.value,
        to: activeEnd.value,
        isUnread: true,
      })
    } else {
      alerts.value = []
    }
  }

  const clearMaintenanceTimer = (): void => {
    if (!timerId.value) return

    clearTimeout(timerId.value)

    timerId.value = undefined
  }

  const clearMaintenance = (): void => {
    clearMaintenanceTimer()

    alerts.value = []
  }

  const maintenanceTimer = (request: AppSetting[]): void => {
    clearMaintenanceTimer()

    if (!intervalMinutes.value) return

    timerId.value = setTimeout(async () => {
      await loadAppSettings(request)
      maintenanceTimer(request)
    }, minutesToMilliseconds(intervalMinutes.value))
  }

  const initApp = async (): Promise<void> => {
    const request = [
      AppSetting.MaintenanceIdleIntervalMinutes,
      AppSetting.MaintenanceActiveIntervalMinutes,
      AppSetting.MaintenanceReminderFromMinutes,
      AppSetting.MaintenanceActiveFromUtcFormat,
      AppSetting.MaintenanceActiveToUtcFormat,
      AppSetting.DeliveryNoteB2BUrlTemplate,
      AppSetting.DeliveryNoteB2CUrlTemplate,
    ]

    await loadAppSettings(request)

    maintenanceTimer(request)
  }

  const loadClientFilter = async (): Promise<void> => {
    await clientStore.loadClientActions()
    await clientStore.loadClientCarriers()
    await clientStore.loadClientCountries()
    await clientStore.loadClientDimensions()
    await clientStore.loadClientServiceGroups()
    await clientStore.loadClientZipZones()

    await filterStore.loadFilter()
  }

  const isViewReady = async (routeName: RouteRecordNameGeneric): Promise<void> => {
    await clientStore.loadClientSettings(routeName)

    await loadClientFilter()

    await motionStore.loadStatuses()
    await dashboardstore.loadSettings()
  }

  const isClientReady = async (routeName: RouteRecordNameGeneric): Promise<void> => {
    await initApp()

    const isReady = await clientStore.isClientViewsReady()

    if (!isReady) return

    await isViewReady(routeName)
  }

  const clearAll = (): void => {
    clearMaintenance()

    authStore.identity = undefined
    userStore.user = undefined
    userStore.notifications = []
  }

  return {
    alerts,
    activeStart,
    activeEnd,
    isLoading,
    isIndeterminate,
    isNavExpanded,
    isFilterOpen,
    isInfoOpen,
    isMaintenanceActive,
    isReminderActive,
    deliveryNoteB2BUrlTemplate,
    deliveryNoteB2CUrlTemplate,
    loadClientFilter,
    isClientReady,
    isViewReady,
    showAlert,
    initApp,
    clearAll,
  }
})
